import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Spinner, Table } from "reactstrap";
import cx from "classnames";
import styles from "./style.module.scss";
import {
    DiagnosableModel,
    EDocumentDiagnosticSource,
    loadDocumentForDiagnostic,
} from "@/api/EDocumentDiagnosticApi";
import { CabysLookup, distinctCabysCodes, resolveCabysCodes } from "@utils/cabysCatalog";
import {
    buildLineDiagnostics,
    buildReceptorDiagnostics,
    buildTotalsReconciliation,
    LINE_ISSUE_LABELS,
    LINE_ISSUE_MESSAGES,
    LineDiagnosticRow,
    productTypeLabel,
    TOTALS_BUCKET_LABELS,
} from "@utils/edocumentDiagnostic";

export type RejectionDiagnosticProps = {
    model: DiagnosableModel;
    documentId: string;
};

const amountFormatter = new Intl.NumberFormat("es-CR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const formatAmount = (value: number): string =>
    amountFormatter.format(Number(value || 0)).replace(/\u00A0/g, " ");

const formatRate = (rate?: number): string => (rate === undefined ? "?" : `${rate}%`);

/**
 * Traduce un rechazo de Hacienda a los datos concretos del documento que hay que corregir.
 *
 * Se monta solo cuando el offcanvas esta abierto sobre un documento rechazado: carga las lineas
 * con su producto del catalogo y consulta la tarifa oficial de cada CAByS en Hacienda. Si el
 * catalogo no responde el resto del diagnostico igual se muestra.
 */
const RejectionDiagnostic = (props: RejectionDiagnosticProps) => {
    const { model, documentId } = props;

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [loadError, setLoadError] = useState<string>("");
    const [source, setSource] = useState<EDocumentDiagnosticSource>();
    const [cabysInfo, setCabysInfo] = useState<CabysLookup>(new Map());
    const [isCatalogComplete, setIsCatalogComplete] = useState<boolean>(true);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            setLoadError("");
            try {
                const document = await loadDocumentForDiagnostic(model, documentId);
                if (cancelled) return;
                setSource(document);

                const codes = distinctCabysCodes(
                    document.lines.map((line) => line.product?.codigo_cabys || line.codigo_cabys)
                );
                const lookup = await resolveCabysCodes(codes);
                if (cancelled) return;
                setCabysInfo(lookup);
                setIsCatalogComplete(lookup.size === codes.length);
            } catch (error) {
                if (cancelled) return;
                const message =
                    error instanceof Error ? error.message : "No fue posible cargar el documento";
                setLoadError(message);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [model, documentId]);

    const lineRows = useMemo(
        () => (source ? buildLineDiagnostics(source.lines, cabysInfo, source.lineOptions) : []),
        [source, cabysInfo]
    );

    const totalsRows = useMemo(
        () => (source ? buildTotalsReconciliation(source.lines) : []),
        [source]
    );

    const receptorIssues = useMemo(
        () => (source ? buildReceptorDiagnostics(source.header, source.client) : []),
        [source]
    );

    const problemCount = lineRows.filter((row) => row.issues.length > 0).length;

    if (isLoading) {
        return (
            <div className={styles.section} data-testid="rejection-diagnostic-loading">
                <Spinner size="sm" /> Revisando las lineas del documento...
            </div>
        );
    }

    if (loadError) {
        return (
            <Alert color="secondary" className={styles.section} data-testid="rejection-diagnostic-error">
                No fue posible revisar el documento: {loadError}
            </Alert>
        );
    }

    return (
        <div className={styles.section} data-testid="rejection-diagnostic">
            <div className={styles.title}>Que revisar en este documento</div>

            {!isCatalogComplete && (
                <Alert color="info" className={styles.hint} data-testid="diagnostic-catalog-warning">
                    No se pudo consultar el catalogo de CAByS de Hacienda, asi que las tarifas no se
                    verificaron. Vuelva a abrir este detalle en unos segundos.
                </Alert>
            )}

            {receptorIssues.map((issue) => (
                <Alert
                    key={issue.code}
                    color="warning"
                    className={styles.hint}
                    data-testid={`diagnostic-receptor-${issue.code}`}
                >
                    {issue.message}
                    {source?.clientId && (
                        <>
                            {" "}
                            <Link to={`/user/client/${source.clientId}`}>Corregir el cliente</Link>
                        </>
                    )}
                </Alert>
            ))}

            {totalsRows.length > 0 && (
                <>
                    <div className={styles.title}>Totales que no le cuadran a Hacienda</div>
                    <p className={styles.hint}>
                        Hacienda reclasifica cada linea por el primer digito de su CAByS y por la tarifa
                        con que se facturo. Estos son los montos que esperaba recibir.
                    </p>
                    <div className="table-responsive">
                        <Table bordered responsive size="sm" className={styles.table} data-testid="diagnostic-totals">
                            <thead>
                                <tr>
                                    <th>Monto</th>
                                    <th className={styles.numeric}>Enviado</th>
                                    <th className={styles.numeric}>Esperado por Hacienda</th>
                                    <th className={styles.numeric}>Diferencia</th>
                                </tr>
                            </thead>
                            <tbody>
                                {totalsRows.map((row) => (
                                    <tr key={row.bucket} data-testid={`diagnostic-total-${row.bucket}`}>
                                        <td>{TOTALS_BUCKET_LABELS[row.bucket]}</td>
                                        <td className={styles.numeric}>{formatAmount(row.sent)}</td>
                                        <td className={cx(styles.numeric, styles.expected)}>
                                            {formatAmount(row.expected)}
                                        </td>
                                        <td className={styles.numeric}>{formatAmount(row.difference)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </>
            )}

            <div className={styles.title}>Lineas del documento</div>
            <p className={styles.hint} data-testid="diagnostic-lines-summary">
                {problemCount > 0
                    ? `${problemCount} de ${lineRows.length} lineas tienen datos que Hacienda rechaza.`
                    : "Ninguna linea tiene el CAByS, el tipo o la tarifa mal configurados."}
            </p>
            <div className="table-responsive">
                <Table bordered responsive size="sm" className={styles.table} data-testid="diagnostic-lines">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Codigo comercial</th>
                            <th>Descripcion</th>
                            <th>CAByS</th>
                            <th>Tipo</th>
                            <th>Tarifa</th>
                            <th>Estado</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lineRows.length === 0 && (
                            <tr>
                                <td colSpan={7} className="text-center">
                                    El documento no tiene lineas
                                </td>
                            </tr>
                        )}
                        {lineRows.map((row) => renderLineRow(row))}
                    </tbody>
                </Table>
            </div>
        </div>
    );
};

const renderLineRow = (row: LineDiagnosticRow) => {
    const hasIssues = row.issues.length > 0;

    return (
        <tr
            key={row.lineNumber}
            className={cx({ [styles.invalidRow]: hasIssues })}
            data-testid={`diagnostic-line-${row.lineNumber}`}
        >
            <td>{row.lineNumber}</td>
            <td data-testid={`diagnostic-line-codigo-${row.lineNumber}`}>
                {row.productId ? (
                    <Link to={`/user/product/${row.productId}`}>{row.codigo || "(sin codigo)"}</Link>
                ) : (
                    row.codigo || "-"
                )}
            </td>
            <td>{row.description || "-"}</td>
            <td>
                {row.cabys || "-"}
                {row.lineCabys && (
                    <div>
                        <small>En la linea quedo {row.lineCabys}</small>
                    </div>
                )}
            </td>
            <td>
                {productTypeLabel(row.sentProductType)}
                {row.issues.includes("type_mismatch") && (
                    <div>
                        <small className={styles.expected}>
                            Hacienda lo lee como {productTypeLabel(row.expectedProductType)}
                        </small>
                    </div>
                )}
            </td>
            <td>
                {formatRate(row.sentTaxRate)}
                {(row.issues.includes("tax_rate_mismatch") || row.issues.includes("exempt_mismatch")) && (
                    <div>
                        <small className={styles.expected}>
                            El CAByS grava al {formatRate(row.catalogTaxRate)}
                        </small>
                    </div>
                )}
            </td>
            <td>
                {!hasIssues && "Correcta"}
                {row.issues.map((issue) => (
                    <span key={issue} className={styles.issueBadge} title={LINE_ISSUE_MESSAGES[issue]}>
                        {LINE_ISSUE_LABELS[issue]}
                    </span>
                ))}
            </td>
        </tr>
    );
};

export default RejectionDiagnostic;
